import React, { useEffect, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform, useAnimationFrame } from "framer-motion";
import { Link } from "react-router-dom";

import StarryBackground from "@/components/layout/StarryBackground";
import WorkspaceHeader from "@/components/layout/WorkspaceHeader";
import { SidebarProvider } from "@/components/ui/sidebar";


const FloatingRoadmapShip: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  // angle in radians that we will animate over time
  const angle = useMotionValue(0);

  // ellipse radii
  const radiusX = 70; // wider = flatter ellipse
  const radiusY = 28; // taller = more vertical motion

  // derive x,y from the angle to follow an ellipse
  const x = useTransform(angle, (a) => Math.cos(a) * radiusX);
  const y = useTransform(angle, (a) => Math.sin(a) * radiusY);

  // smooth continuous animation
  useAnimationFrame((t) => {
    const speed = 0.0017; // tweak for faster / slower orbit
    const loopAngle = (t * speed) % (Math.PI * 2);
    angle.set(loopAngle);
  });

  return (
    <motion.div
      className="
        fixed z-30
        top-[18vh] right-[8vw]
        pointer-events-auto
      "
      style={{
        x,
        y,
        rotate: -3,
      }}
    >
      <motion.button
        type="button"
        onClick={onClick}
        whileHover={{ scale: 1.06, y: -1 }}
        whileTap={{ scale: 0.97 }}
        className="
          relative
          flex items-center gap-3
          rounded-full
          px-6 py-2.5
          bg-black/70
          border border-calcera-purple/70
          shadow-[0_0_26px_rgba(168,85,247,0.9)]
          backdrop-blur-xl
        "
        aria-label="Open Calcera roadmap"
      >
        {/* subtle purple glow */}
        <div
          className="pointer-events-none absolute inset-0 rounded-full"
          style={{
            background:
              "radial-gradient(circle at 50% 0%, rgba(168,85,247,0.7), transparent 60%)",
            filter: "blur(10px)",
          }}
        />

        {/* left accent dot */}
        <div className="relative flex items-center justify-center">
          <div className="h-7 w-7 rounded-full bg-gradient-to-br from-calcera-purple via-fuchsia-500 to-indigo-500 shadow-[0_0_20px_rgba(168,85,247,1)]" />
        </div>

        {/* text */}
        <div className="relative flex flex-col items-start leading-none">
          <span className="text-[0.6rem] uppercase tracking-[0.22em] text-white/60">
            Calcera
          </span>
          <span className="mt-[2px] text-[0.9rem] font-semibold uppercase tracking-[0.3em] text-white drop-shadow-[0_0_6px_rgba(0,0,0,0.9)]">
            Roadmap
          </span>
        </div>
      </motion.button>
    </motion.div>
  );
};


export default function AboutCalcera() {
  const [showRoadmap, setShowRoadmap] = useState(false);

  const renderNumberList = (items: string[]) => (
    <div className="text-white/80 text-base md:text-lg space-y-4 max-w-xl text-left leading-relaxed mx-auto mt-5">
      {items.map((text, i) => (
        <div key={i} className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-calcera-purple/25 border border-calcera-purple text-calcera-purple font-semibold flex items-center justify-center text-xs md:text-sm shadow-[0_0_8px_rgba(168,85,247,0.5)]">
            {i + 1}
          </div>
          <p className="flex-1 leading-relaxed text-base md:text-lg">{text}</p>
        </div>
      ))}
    </div>
  );

  const slides = [
    {
      title: "Why Calcera?",
      content: (
        <div className="space-y-4 text-white/85 text-base md:text-lg leading-relaxed max-w-2xl mx-auto text-left">
            <p className="text-center">
              Calcera ensures MEP project data is captured, retrievable, and protected.
            </p>

            {renderNumberList([
              "Engineering work is captured once - not scattered across notepads, emails, and spreadsheets",
              "Find past decisions, calculations, and context in seconds.",
              "Engineering tools and project data don't walk out with individuals.",
            ])}

            <p className="text-center text-calcera-purple/90 font-semibold tracking-wide mt-4">
              Here to keep engineers in control while reducing busywork and rework.
            </p>
        </div>
      ),
    },
    {
      title: "Engineered for Connected MEP Workflows",
      content: (
        <div className="max-w-2xl mx-auto">
          <p className="text-white/85 text-base md:text-lg leading-relaxed text-center">
            Calcera connects every role involved in a project around a single, shared source of engineering data.
          </p>
          {renderNumberList([
            "One system shared by engineers, project managers, department heads, and reviewers.",
            "One source of truth for project data and decisions.",
            "Eliminates disconnected tools, files, and delays caused by missing information.",
          ])}
        </div>
      ),
    },
    {
      title: "Optimize Time and Precision",
      content: (
        <div className="max-w-2xl mx-auto">
          <p className="text-white/85 text-base md:text-lg leading-relaxed text-center">
            Spend less time managing files and more time solving problems. <br />
            Calcera keeps work clean, organized, and easy to trace later.
          </p>
          {renderNumberList([
            "Run calculations instantly without flipping through references.",
            "Notes, decisions, and calculations save and sort as you work.",
            "No more organizing calculation files, notes, or spreadsheets.",
            "Push drawings forward with logic that is traceable.",
          ])}
        </div>
      ),
    },
    {
      title: "Data-Driven Project Insights",
      content: (
        <div className="max-w-2xl mx-auto">
          <p className="text-white/85 text-base md:text-lg leading-relaxed text-center">
            Your daily work becomes usable data, with no extra effort, exports, or spreadsheets.
          </p>
          {renderNumberList([
            "See project progress at a glance, not after the fact.",
            "Spot delays early instead of reacting when it is urgent.",
            "Turn everyday actions into metrics you can use.",
            "Give leaders clarity without building manual reports.",
          ])}
        </div>
      ),
    },
    {
      title: "Business Value and Quality",
      content: (
        <div className="max-w-2xl mx-auto">
          <p className="text-white/85 text-base md:text-lg leading-relaxed text-center">
            Manage project work and data without digging through old files or folders. Everything you need is saved, searchable, and easy to reference.
          </p>
          {renderNumberList([
            "Time saved goes back into real billable production.",
            "Track progress and trends instead of reacting at the end.",
            "Your project history becomes real data, not scattered memories.",
            "Less chasing information means less designer stress.",
          ])}
        </div>
      ),
    },
    {
      title: "Built by Engineers, for Engineers",
      content: (
        <div className="max-w-2xl mx-auto">
          <p className="text-white/85 text-base md:text-lg leading-relaxed text-center">
            Calcera is informed by real engineering experience and is shaped around the actual challenges that design teams face on everyday projects.
          </p>

          {renderNumberList([
            "Calculations follow recognized industry codes and standards.",
            "Features reflect real coordination needs, changing requirements, and documentation expectations.",
            "The platform evolves through ongoing feedback from individuals actively involved in design work.",
          ])}
        </div>
      ),
    },
    {
      title: "Disclaimer",
      content: (
        <div className="max-w-2xl mx-auto">
          <p className="text-white/85 text-base md:text-lg leading-relaxed text-center">
            Calcera supports engineering decisions, but it does not replace the judgment, review, or responsibility of licensed professionals.
          </p>

          {renderNumberList([
            "Verify all results against current codes and project conditions.",
            "Local requirements may differ from examples shown.",
            "Final approval and stamping always remain with the design team.",
          ])}
        </div>
      ),
    },
  ];

  const [index, setIndex] = useState(0);
  const [orbitAngle, setOrbitAngle] = useState(0);

  const rx = 650;
  const ry = 330;
  const orbitYOffset = -25;

  useEffect(() => {
    const id = setInterval(() => setOrbitAngle((prev) => prev + 0.18), 25);
    return () => clearInterval(id);
  }, []);

  return (
    <SidebarProvider>
      <StarryBackground variant="full" showParticles showPrompts={false} />

      <div className="fixed left-0 right-0 top-0 z-40 flex flex-col">
        <WorkspaceHeader />
      </div>

      {/* Floating satellite roadmap trigger */}
      <FloatingRoadmapShip onClick={() => setShowRoadmap(true)} />

      <div className="fixed inset-0 top-[96px] flex flex-col items-center justify-center text-white z-20">
        {/* ORBITING BUBBLES */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 flex items-center justify-center">
            {slides.map((slide, i) => {
              const total = slides.length;

              const angle = (orbitAngle + i * (360 / total)) * (Math.PI / 180);
              const x = Math.cos(angle) * rx;
              const yBase = Math.sin(angle) * ry;
              const y = yBase + orbitYOffset;

              const minScale = 0.6;
              const maxScale = 1.2;
              const normalizedY = (yBase + ry) / (2 * ry);
              const scale = minScale + (maxScale - minScale) * normalizedY;
              const zIndex = Math.round(scale * 100);

              return (
                <motion.div
                  key={i}
                  className="absolute pointer-events-auto"
                  style={{ x, y, scale, zIndex }}
                >
                  <motion.button
                    onClick={() => setIndex(i)}
                    transition={{ type: "spring", stiffness: 240, damping: 18 }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className={`
                      w-[130px] h-[130px]
                      rounded-full flex items-center justify-center text-center select-none
                      border backdrop-blur-xl
                      transition-all text-[0.85rem] font-semibold leading-tight px-4
                      ${
                        index === i
                          ? "border-calcera-purple bg-calcera-purple/40 text-white shadow-[0_0_40px_rgba(168,85,247,0.9)]"
                          : "border-calcera-purple/40 text-purple-300 hover:bg-calcera-purple/20 hover:shadow-[0_0_20px_rgba(168,85,247,0.5)]"
                      }
                    `}
                  >
                    {slide.title}
                  </motion.button>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* CENTER CARD */}
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.94 }}
            transition={{ duration: 0.45 }}
            className="
              bg-black/40 backdrop-blur-xl rounded-2xl border border-calcera-purple/30 
              shadow-[0_0_40px_rgba(168,85,247,0.45)]
              w-[660px] max-w-[88vw] py-4 px-9 text-center
              -mt-7
            "
          >
            <h1 className="text-xl md:text-2xl font-extrabold text-calcera-purple mb-4 tracking-wide">
              {slides[index].title}
            </h1>

            <div>{slides[index].content}</div>
          </motion.div>
        </AnimatePresence>

        {/* HOME BUTTON */}
        <Link
          to="/"
          className="
            group relative mt-10
            px-12 py-3 rounded-full uppercase tracking-[0.3em] text-[13px] font-semibold
            text-white
            bg-gradient-to-b from-calcera-purple/40 to-calcera-purple/20
            border border-white/25 backdrop-blur-sm
            shadow-[0_0_10px_rgba(168,85,247,0.8)]
            hover:shadow-[0_0_30px_rgba(168,85,247,0.95)]
            hover:border-white/40 hover:bg-calcera-purple/40
            transition-all duration-300 ease-out
          "
        >
          <span
            className="
              group-hover:tracking-[0.4em]
              transition-all duration-300 ease-out
            "
          >
            Home
          </span>

          {/* Glow aura highlight */}
          <span
            className="
              absolute inset-0 rounded-full
              bg-[radial-gradient(circle_at_center,rgba(168,85,247,0.7),transparent)]
              opacity-0 group-hover:opacity-100
              blur-xl transition-opacity duration-300 pointer-events-none
            "
          />
        </Link>

      </div>

      {/* Roadmap panel */}
      {showRoadmap && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-2xl"
            onClick={() => setShowRoadmap(false)}
          />

          {/* Roadmap panel */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.25 }}
            className="
              relative z-50
              w-[960px] max-w-[94vw] max-h-[82vh]
              rounded-[32px]
              border border-calcera-purple/60
              bg-[radial-gradient(circle_at_top,_rgba(88,28,135,0.45),_rgba(0,0,0,0.96))]
              shadow-[0_0_75px_rgba(168,85,247,0.95)]
              flex flex-col
            "
          >
            {/* Header */}
            <div className="relative rounded-[32px] border-b border-white/10 bg-black/80 px-6 py-4">
              {/* Close pill button - top right */}
              <button
                type="button"
                onClick={() => setShowRoadmap(false)}
                className="
                  absolute right-6 top-4
                  inline-flex items-center justify-center
                  rounded-full px-4 py-1.5
                  text-[0.7rem] md:text-xs font-semibold tracking-[0.16em] uppercase
                  bg-calcera-purple text-white/90
                  hover:bg-red-500/25
                  hover:text-red-400
                  transition
                "
              >
                Close
              </button>

              {/* Centered content */}
              <div className="flex flex-col items-center gap-2">
                <div className="flex flex-col items-center gap-2">

              {/* Title */}
              <h2
                className="
                  text-lg md:text-xl
                  font-extrabold
                  uppercase tracking-[0.45em]
                  text-white
                  drop-shadow-[0_0_18px_rgba(168,85,247,0.9)]
                "
              >
                Roadmap
              </h2>

              {/* Accent line */}
              <div
                className="
                  h-[2px] w-24
                  bg-gradient-to-r from-transparent via-calcera-purple to-transparent
                  shadow-[0_0_16px_rgba(168,85,247,0.9)]
                "
              />
            </div>
                <p className="text-[0.7rem] md:text-xs text-white/70 text-center max-w-xl">
                  From the present state of the platform, to the next phase of development, to the long-term vision.
                </p>
              </div>
            </div>

            {/* Body */}
            <div className="p-5 md:p-6 flex justify-center items-center h-full">
              <div className="flex w-full max-w-4xl">
                {/* Timeline column */}
                <div className="relative flex flex-col items-center justify-center mr-8 md:mr-10 py-2 min-w-[140px]">
                  {/* Soft vertical glow */}
                  <div className="absolute inset-y-0 w-[120px] bg-gradient-to-b from-calcera-purple/24 via-transparent to-calcera-purple/18 blur-2xl pointer-events-none" />

                  {/* Central beam */}
                  <motion.div
                    className="absolute top-4 bottom-4 w-[3px] rounded-full bg-gradient-to-b from-calcera-purple via-calcera-purple/60 to-calcera-purple/10 shadow-[0_0_14px_rgba(168,85,247,0.65)]"
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                  />

                  <div className="flex flex-col items-center gap-4">
                    {[
                      { label: "Now Live", size: 90 },
                      { label: "Next Up", size: 90 },
                      { label: "Planned", size: 90 },
                      { label: "Vision", size: 90 },
                    ].map((step, index) => (
                      <motion.div
                        key={step.label}
                        className="relative z-10"
                        animate={{ y: [-10, 20, -10] }}
                        transition={{
                          duration: 1.8,
                          repeat: Infinity,
                          ease: "easeInOut",
                          delay: index * 0.18,
                        }}
                      >
                        <div
                          className="
                            relative flex items-center justify-center
                            rounded-full border border-calcera-purple/70
                            bg-black/70 backdrop-blur-lg
                            shadow-[0_0_22px_rgba(168,85,247,0.8)]
                            hover:shadow-[0_0_32px_rgba(168,85,247,1)]
                            transition-all duration-300
                          "
                          style={{ width: step.size, height: step.size }}
                        >
                          {/* glow halo */}
                          <span className="absolute inset-[-8px] rounded-full bg-calcera-purple/40 blur-xl opacity-70" />

                          {/* label */}
                          <span className="relative uppercase tracking-[0.22em] text-xs font-semibold text-white/95 text-center leading-tight">
                            {step.label}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Text column */}
                <div className="flex-1 space-y-3.5 text-white/90 text-[0.78rem] md:text-sm">
                  {/* NOW */}
                  <motion.div
                    className="relative rounded-2xl bg-black/65 border border-white/14 px-4 py-3 md:px-5 md:py-4 transition-transform hover:scale-[1.01] hover:border-calcera-purple/70 hover:shadow-[0_0_30px_rgba(168,85,247,0.7)]"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.24, delay: 0.05 }}
                  >
                    <div className="absolute left-0 top-4 bottom-4 w-[2px] bg-gradient-to-b from-calcera-purple/80 via-calcera-purple/40 to-transparent rounded-full opacity-70" />
                    <div className="pl-3">
                      <div className="flex flex-wrap items-center gap-2.5 mb-1.5">
                        
                        <span className="inline-flex items-center rounded-full bg-calcera-purple/25 border border-calcera-purple/70 px-3 py-0.5 text-[0.65rem] uppercase tracking-[0.16em] text-calcera-purple-foreground">
                          Available now
                        </span>
                      </div>

                      <ul className="list-disc list-inside space-y-0.5 leading-relaxed">
                        <li>Work inside a single, cohesive engineering workspace built for traceability.</li>
                        <li>Capture decisions, notes, and tasks tied directly to when and why they occured.</li>
                      </ul>
                    </div>
                  </motion.div>

                  {/* NEXT */}
                  <motion.div
                    className="relative rounded-2xl bg-black/60 border border-white/12 px-4 py-3 md:px-5 md:py-4 transition-transform hover:scale-[1.01] hover:border-yellow-300/80 hover:shadow-[0_0_26px_rgba(250,204,21,0.7)]"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.24, delay: 0.12 }}
                  >
                    <div className="absolute left-0 top-4 bottom-4 w-[2px] bg-gradient-to-b from-yellow-300/90 via-yellow-300/40 to-transparent rounded-full opacity-80" />
                    <div className="pl-3">
                      <div className="flex flex-wrap items-center gap-2.5 mb-1.5">
                        
                        <span className="inline-flex items-center rounded-full bg-yellow-400/18 border border-yellow-300/80 px-3 py-0.5 text-[0.65rem] uppercase tracking-[0.16em] text-yellow-50">
                          Being built
                        </span>
                      </div>

                      <ul className="list-disc list-inside space-y-0.5 leading-relaxed">
                        <li>Drag-and-drop custom calculator builder tailored to firm-specific workflows.</li>
                        <li>Building our default calculator library to cover foundational engineering use cases.</li>
                        <li>AI-generated meeting notes automatically saved to a specific project.</li>
                      </ul>
                    </div>
                  </motion.div>

                  {/* LATER */}
                  <motion.div
                    className="relative rounded-2xl bg-black/60 border border-white/12 px-4 py-3 md:px-5 md:py-4 transition-transform hover:scale-[1.01] hover:border-white/80 hover:shadow-[0_0_26px_rgba(255,255,255,0.45)]"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.24, delay: 0.19 }}
                  >
                    <div className="absolute left-0 top-4 bottom-4 w-[2px] bg-gradient-to-b from-white/80 via-white/30 to-transparent rounded-full opacity-80" />
                    <div className="pl-3">
                      <div className="flex flex-wrap items-center gap-2.5 mb-1.5">
                        
                        <span className="inline-flex items-center rounded-full bg-white/14 border border-white/60 px-3 py-0.5 text-[0.65rem] uppercase tracking-[0.16em] text-white">
                          Planned
                        </span>
                      </div>

                      <ul className="list-disc list-inside space-y-0.5 leading-relaxed">
                        <li>ICC code integration for easy code reference access.</li>                        
                        <li>Document comparison for detecting submittal, schedule, standards, and spec inconsistencies.</li>
                        <li>Site visit tools that automatically organize field notes and photos by project.</li>
                      </ul>
                    </div>
                  </motion.div>

                  {/* VISION */}
                  <motion.div
                    className="relative rounded-2xl bg-black/60 border border-white/12 px-4 py-3 md:px-5 md:py-4 transition-transform hover:scale-[1.01] hover:border-calcera-purple/80 hover:shadow-[0_0_28px_rgba(168,85,247,0.8)]"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.24, delay: 0.26 }}
                  >
                    <div className="absolute left-0 top-4 bottom-4 w-[2px] bg-gradient-to-b from-calcera-purple/90 via-calcera-purple/40 to-transparent rounded-full opacity-80" />
                    <div className="pl-3">
                      <div className="flex flex-wrap items-center gap-2.5 mb-1.5">
                        
                        <span className="inline-flex items-center rounded-full bg-calcera-purple/22 border border-calcera-purple/70 px-3 py-0.5 text-[0.65rem] uppercase tracking-[0.16em] text-calcera-purple-foreground">
                          Exploring
                        </span>
                      </div>

                      <ul className="list-disc list-inside space-y-0.5 leading-relaxed">
                        <li>A searchable engineering record spanning projects, people, and decisions.</li>
                        <li>Proactive flags for potential issues, overloads, and coordination risks before they become problems.</li>
                        <li>Built-in accounting and project tracking to monitor schedules, budgets, and hours alongside the design.</li>
                      </ul>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </SidebarProvider>
  );
}
